// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Render one protected reference for a viewer.
 *
 * An **unrestricted** reference is returned in full regardless of `authorized`.
 * That is deliberate and it is the difference between a control and a
 * ceremony: if every reference required a grant, tenants would grant broadly to
 * make the product usable, and the grant would stop meaning anything for the
 * references that matter. `restricted` is set by the pack, at write time, per
 * kind — so the regime decides what needs protecting, not this function.
 */
export function redactProtectedReference(reference, authorized) {
    const withhold = reference.restricted && !authorized;
    return {
        kind_code: reference.kind_code,
        custodian: reference.custodian,
        external_reference: withhold ? null : reference.external_reference,
        restricted: reference.restricted,
        is_redacted: withhold,
        recorded_at: reference.recorded_at,
        recorded_by: reference.recorded_by,
    };
}
/** The list form. Order is preserved — a caller's sort is its own business. */
export function redactProtectedReferences(references, authorized) {
    return references.map((r) => redactProtectedReference(r, authorized));
}
/**
 * The value an export writes where a redacted handle would have gone.
 *
 * A blank cell reads as "the organisation holds nothing", which is a different
 * and materially wrong compliance statement. An inspector reading an export
 * must be able to tell that a reference exists and was withheld from this
 * viewer.
 */
export const REDACTED_EXPORT_PLACEHOLDER = '[redacted — authorization required]';
/**
 * Render a reference for a spreadsheet or document cell.
 *
 * Kept beside the primitive rather than in the export service, so the read path
 * and the export path cannot drift into two different notions of what redaction
 * looks like.
 */
export function protectedReferenceToCell(view) {
    if (view.is_redacted)
        return REDACTED_EXPORT_PLACEHOLDER;
    return `${view.custodian}: ${view.external_reference ?? ''}`;
}
/** What every constructor here emits where nothing may be shown. */
export const MASK_SUFFIX = '***';
/** The value a mask constructor returns when it can reveal nothing at all. */
export const FULLY_MASKED = MASK_SUFFIX;
/**
 * Absorbed from `aesirx-client.ts:1203`'s `redactCredentialId`, which was
 * "first 8 chars + `***`" with the 8 hard-coded. One line of behaviour and
 * three characters of policy, and no reason for it to be module-private.
 *
 * `visiblePrefixLength` is **required**. It is the whole of the policy: a
 * default would be a decision made once, in this file, for callers whose
 * identifiers this file has never seen. `redaction.ts` already takes that
 * position for `authorized`, and takes it again here.
 *
 * ## One deliberate behaviour change from the absorbed original
 *
 * The original returned `value + '***'` when `value` was shorter than the
 * prefix width — the *entire* identifier, presented as though masked. For a
 * 26-character ULID credential id that never happened; for a short id it is a
 * full disclosure wearing a mask's clothes, which is worse than no mask because
 * a reviewer sees the `***` and stops looking. It now returns
 * {@link FULLY_MASKED}, revealing nothing. No existing call site's output
 * changes: every input in the corpus is longer than its prefix width, asserted
 * in `redaction.test.ts`.
 *
 * @throws if `visiblePrefixLength` is not a positive integer — a zero or
 * negative width is a caller bug, and silently coercing it to "show nothing"
 * would hide the bug behind a correct-looking output.
 */
export function maskIdentifierPrefix(value, visiblePrefixLength) {
    if (!Number.isInteger(visiblePrefixLength) || visiblePrefixLength < 1) {
        throw new RangeError(`maskIdentifierPrefix: visiblePrefixLength must be a positive integer, received ${String(visiblePrefixLength)}`);
    }
    if (value.length <= visiblePrefixLength)
        return FULLY_MASKED;
    return `${value.slice(0, visiblePrefixLength)}${MASK_SUFFIX}`;
}
/**
 * Absorbed from `response-delivery-service.ts:92`'s email arm: `a***@domain`.
 *
 * The *dispatch* around it stays domain-locked in rights-requests (it has a
 * `method` parameter no other caller can supply — see AC6). This arm is not
 * domain-locked: masking an email local part while keeping the domain is a
 * general operation, and leaving it inside the dispatch is what would force the
 * next caller to either import a function with a `method` parameter or write a
 * fifth regex.
 *
 * The domain is kept deliberately. An audit line that cannot distinguish
 * `s***@gmail.com` from `s***@ministry.gov.vn` cannot answer the question an
 * audit line exists to answer.
 *
 * A value with no `@` is masked whole rather than passed through: an input that
 * is not an address is an input this function cannot reason about, and passing
 * it through unchanged is the fail-open branch.
 *
 * ## A one-character local part reveals the whole local part
 *
 * {@link maskIdentifierPrefix} already refuses to keep a prefix as wide as the
 * value it is masking, for the reason its docblock gives: a full disclosure
 * wearing a mask's clothes is worse than no mask, because a reviewer sees the
 * `***` and stops looking. The email arm kept one character unconditionally, so
 * `a@ministry.gov.vn` came back as `a***@ministry.gov.vn` — the complete
 * original address — and a two-character local part gave up half of itself.
 * `isMaskableEmailAddress` accepts both (it requires only a non-empty local
 * part), so the fail-closed gate above never fired for them. The same rule now
 * applies here: when keeping one character would keep all of them, keep none.
 * The domain is still preserved, per the audit requirement stated above.
 */
export function maskEmailLocalPart(email) {
    if (!isMaskableEmailAddress(email))
        return FULLY_MASKED;
    const at = email.indexOf('@');
    const local = email.slice(0, at);
    if (local.length <= 1)
        return `${MASK_SUFFIX}${email.slice(at)}`;
    return `${local.slice(0, 1)}${MASK_SUFFIX}${email.slice(at)}`;
}
/**
 * Whether a value is an address {@link maskEmailLocalPart} can actually parse.
 *
 * ## Why this is exported, and why the caller must use *this* predicate
 *
 * Masking was not the only thing that failed in the defect this replaces. The
 * pre-sprint mask returned its input unchanged when its regex did not match —
 * fail-open — and **nothing upstream had rejected the input either**: the
 * rights-request delivery route validated only that `recipient` was an object,
 * and the service only that `recipient.email` was truthy. So an operator who
 * typed a name, a phone number or a note into the recipient field had it
 * written verbatim into an audit event and into a downloadable proof file.
 *
 * A validator and a mask that disagree recreate exactly that gap one step over:
 * the route accepts something the mask cannot parse, and the mask then has to
 * decide what to do with it. Here they cannot disagree, because the mask calls
 * this predicate and the caller is expected to call the same one. "The route
 * accepts it" and "the mask can parse it" are one statement by construction.
 *
 * Deliberately conservative rather than RFC-complete: exactly one `@`, a
 * non-empty local part, a non-empty domain, and no whitespace anywhere. It
 * rejects `John Smith <a@b.com>` — a display-name form that is not an address
 * and that the platform has no business sending to — and every free-text note
 * the operator field actually received. It is **not** a deliverability check
 * and does not pretend to be one.
 */
export function isMaskableEmailAddress(value) {
    if (/\s/.test(value))
        return false;
    const at = value.indexOf('@');
    if (at < 1 || at !== value.lastIndexOf('@'))
        return false;
    return at < value.length - 1;
}
/**
 * The "we are withholding this entirely, and here is what sort of thing it was"
 * form — `postal:<redacted>`, `in_person:<redacted>`.
 *
 * `context` names the *category*, never the value. It exists for the same
 * reason `REDACTED_EXPORT_PLACEHOLDER` is not a blank cell: a reader must be
 * able to tell "nothing was held" from "something was held and withheld", and
 * those are different compliance positions.
 */
export function maskWithheld(context) {
    return (context ? `${context}:<redacted>` : '<redacted>');
}
/* ── The single field classification (AC2) ─────────────────────────────── */
/**
 * ## The one statement of which *key names* are sensitive
 *
 * These are the eleven distinct key names behind `logger.ts`'s pino `redact:`
 * config, which before this sprint was the only corpus-wide statement of the
 * set and was a literal array inside a backend file — unreachable from `shared`
 * and unreachable from the export path.
 *
 * The direction of the dependency is the one thing worth reading carefully.
 * The sprint spec says the classification "imports from" `logger.ts`. It cannot:
 * `shared` may not import `backend`, and inverting the two would be a cycle.
 * So the *names* moved here and `logger.ts` now derives its pino paths from
 * them via {@link buildPinoRedactPaths}. The substance of the requirement — one
 * list, not two, and the pino config still living in `logger.ts` where it is
 * config rather than a function — holds exactly. The alternative that preserves
 * the spec's literal wording is a second hand-written copy in `shared`, which
 * is the defect the requirement exists to prevent.
 *
 * Adding a name here is a compliance judgement with an owner and is **out of
 * scope** for 64.40.0. This is the same eleven names, relocated, plus nothing.
 *
 * ## What "sensitive" means here (fix-sprint 20261031-1200 F22)
 *
 * Credential and secret keys only. This classification makes no claim about
 * personal data; an export that carries personal data says so in its own
 * metadata. Personal data is a separate matter, decided per export, and no
 * list here classifies it.
 */
export const SENSITIVE_FIELD_KEYS = [
    'password',
    'password_hash',
    'token',
    'secret',
    'api_key',
    'credit_card',
    'authorization',
    'cookie',
    // fix-sprint-20260511-2113 — McpApiKey issue/rotate responses carry plaintext
    // credentials for the one-time-display moment. They must never reach the
    // global response logger.
    'plaintext_key',
    'webhook_secret_plaintext',
    'key_plaintext',
];
const SENSITIVE_FIELD_KEY_SET = new Set(SENSITIVE_FIELD_KEYS);
/** Whether a key name is classified sensitive. Case-sensitive, as pino is. */
export function isSensitiveFieldKey(key) {
    return SENSITIVE_FIELD_KEY_SET.has(key);
}
/**
 * Expand the classification into pino `redact.paths` form: the bare key, and
 * the `*.`-prefixed form that catches it one level down.
 *
 * ## This closes a hole rather than reproducing the old array
 *
 * The pre-sprint array carried `*.`-twins for six of its eight base names.
 * `authorization` and `cookie` had **no** twin, so a request logged as
 * `{ headers: { authorization: 'Bearer …' } }` — the shape a bearer token
 * actually arrives in — was matched by nothing. Deriving the paths gives every
 * name a twin, which is strictly more redaction and never less, and is the
 * direction a data-protection change is allowed to move without a migration.
 *
 * Two nested levels (`*.*.token`) are **not** generated. pino's matcher is not
 * recursive and the deep case wants a censor function, which is a different
 * change with a different blast radius; it is recorded in the workfile as a
 * known remaining gap rather than half-built here.
 */
export function buildPinoRedactPaths(keys) {
    return keys.flatMap((key) => [key, `*.${key}`]);
}
/**
 * The derived pino path list. `logger.ts` consumes this and holds no key names
 * of its own — asserted against `logger.ts`'s source by the compiler API, not
 * by a regex over it (R-52).
 */
export const PINO_REDACT_PATHS = buildPinoRedactPaths(SENSITIVE_FIELD_KEYS);
/* ── The type-derived arm of the classification (AC2) ──────────────────── */
/**
 * The other half of "derived from the shared types".
 *
 * A field of `ProtectedReference` is sensitive exactly when
 * `ProtectedReferenceView` widens it to admit `null` — that widening *is* the
 * declaration that the field can be withheld, made once, in the type, by
 * 62.7.0. Restating the set as a hand-written array would be a second thing to
 * drift, so this constant is typed `keyof ProtectedReference` and its
 * membership is checked against the two interfaces by the compiler API in
 * `redaction.test.ts`, in both directions:
 *
 * - a name here that the view does not widen → fails, naming the field;
 * - a field the view widens that is absent here → fails, naming the field.
 *
 * Neither direction can pass by accident, and the second is the one that
 * catches a future `ProtectedReference` field added with a nullable view
 * counterpart and no classification.
 */
export const REDACTABLE_PROTECTED_REFERENCE_FIELDS = [
    'external_reference',
];
/* ══════════════════════════════════════════════════════════════════════════
 * The export path's redaction decision (AC1)
 * ═════════════════════════════════════════════════════════════════════════ */
/**
 * Normalise a human column header to the key vocabulary.
 *
 * An export's tables are labelled for people — `API Key`, `Password Hash`,
 * `Credit Card` — while the classification is keyed on the wire names pino was
 * configured with. This is the one place the two are reconciled, and it is a
 * transformation rather than a second lookup table, so a new classified key
 * needs no new alias entry.
 *
 * `API Key` → `api_key` · `Password Hash` → `password_hash` ·
 * `Credit-Card #` → `credit_card`
 */
export function normalizeFieldLabel(label) {
    return label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}
/**
 * Which classified key a column header denotes, or `null`.
 *
 * Deliberately **exact** after normalisation, never a substring match. A
 * substring rule would classify `Tokens Issued` and `Password Reset Requested
 * At` as secrets, redact ordinary operational columns out of every export, and
 * teach operators that the placeholder means nothing. Over-redaction is not the
 * safe direction here — it is the direction that gets the control switched off.
 *
 * The cost is a real false-negative surface (`access_token`, `client_secret`,
 * `refresh_token` are *not* classified), which is a **compliance judgement with
 * an owner** and explicitly out of 64.40.0's scope. It is measured in the
 * workfile rather than quietly widened here.
 */
export function classifySensitiveLabel(label) {
    const normalized = normalizeFieldLabel(label);
    return isSensitiveFieldKey(normalized) ? normalized : null;
}
/**
 * The caller's assertion that the rows it is exporting carry no classified
 * field at all.
 *
 * ## Why this is not a way to opt out
 *
 * It is a **claim, and the mechanism checks it.** If the redaction step meets a
 * classified column under this decision it does not trust the assertion: it
 * redacts the column anyway and records `assertion_violated`. So the worst
 * outcome of stamping this on an export that turns out to carry a secret is a
 * redacted cell and a flagged record — never a disclosure.
 *
 * That property is what makes the wide migration safe. If `ASSERT_NO_SENSITIVE_FIELDS`
 * were an escape hatch that skipped the step, then 145 call sites would each
 * have to be *right*, and a mechanism whose correctness depends on 145 authors
 * being right is the mechanism this sprint exists to replace.
 *
 * ## What it asserts (fix-sprint 20261031-1200 F22)
 *
 * Credential and secret keys only. This assertion makes no claim about
 * personal data; an export that carries personal data says so in its own
 * metadata. "Sensitive" is {@link SENSITIVE_FIELD_KEYS}: passwords, tokens,
 * secrets, keys, card numbers, authorisation headers, cookies.
 */
export const ASSERT_NO_SENSITIVE_FIELDS = {
    kind: 'no_sensitive_fields',
};
/**
 * Build a positive authorisation.
 *
 * `authorizedFields` is **required** — `authorizeExportFields()` with no
 * argument does not compile. A caller reaching for this constructor is
 * asserting a viewer may see something classified, and "which" is the whole of
 * the assertion.
 */
export function authorizeExportFields(authorizedFields) {
    return { kind: 'viewer_field_authorization', authorizedFields };
}
/**
 * Whether `field` may be emitted in full under `decision`.
 *
 * Fails closed on every path: `ASSERT_NO_SENSITIVE_FIELDS` authorises nothing, and an
 * unrecognised decision shape authorises nothing rather than falling through to
 * `true`.
 */
export function isFieldAuthorizedForExport(decision, field) {
    if (decision.kind === 'viewer_field_authorization') {
        return decision.authorizedFields.includes(field);
    }
    return false;
}
//# sourceMappingURL=redaction.js.map